import React, { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { backendApi } from '../lib/backendApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Search, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { formatBusinessDateTime } from '../lib/dateTime';

const aggregateSaleItems = (items = []) => {
  const byProduct = new Map();
  for (const item of items) {
    const productId = item.product_id;
    if (!productId) continue;
    const qty = Number(item.quantity || 0);
    if (qty <= 0) continue;
    const current = byProduct.get(productId);
    if (!current) {
      byProduct.set(productId, {
        product_id: productId,
        product_name: item.product_name || 'Unknown product',
        sold_qty: qty,
      });
    } else {
      current.sold_qty += qty;
    }
  }
  return Array.from(byProduct.values());
};

const refundedQtyByProduct = (refunds = []) => {
  const out = new Map();
  for (const refund of refunds) {
    for (const item of refund.items || []) {
      const productId = item.product_id;
      const qty = Number(item.quantity || 0);
      if (!productId || qty <= 0) continue;
      out.set(productId, (out.get(productId) || 0) + qty);
    }
  }
  return out;
};

const RefundsPage = () => {
  const { api, permissions } = useAuth();
  const [receiptNumber, setReceiptNumber] = useState('');
  const [sale, setSale] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [rowForm, setRowForm] = useState({});
  const [notes, setNotes] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');

  const canIssueRefunds = permissions?.issue_refunds !== false;

  const rows = useMemo(() => {
    if (!sale) return [];
    const sold = aggregateSaleItems(sale.items || []);
    const refunded = refundedQtyByProduct(refunds);
    return sold.map((row) => {
      const refundedQty = refunded.get(row.product_id) || 0;
      return {
        ...row,
        refunded_qty: refundedQty,
        remaining_qty: Math.max(0, row.sold_qty - refundedQty),
      };
    });
  }, [sale, refunds]);

  const saleProductNames = useMemo(() => {
    const names = new Map();
    for (const item of sale?.items || []) {
      if (item.product_id && item.product_name) {
        names.set(item.product_id, item.product_name);
      }
    }
    return names;
  }, [sale]);

  const formatCurrency = (amount) => `UGX ${Number(amount || 0).toLocaleString('en-UG', { maximumFractionDigits: 0 })}`;

  const getRefundProductName = (item) => (
    saleProductNames.get(item.product_id) || item.product_name || 'Unknown product'
  );

  const getRefundReference = (index) => `REF-${String(refunds.length - index).padStart(3, '0')}`;

  const loadSaleByReceipt = async () => {
    const receipt = receiptNumber.trim();
    if (!receipt) {
      toast.error('Enter a receipt number');
      return;
    }
    setLookupLoading(true);
    setError('');
    setSale(null);
    setRefunds([]);
    setRowForm({});
    setNotes('');
    try {
      const saleRes = await backendApi.sales.byReceipt(api, receipt);
      const foundSale = saleRes.data;
      setSale(foundSale);
      const refundsRes = await backendApi.sales.listRefunds(api, foundSale.id);
      setRefunds(refundsRes.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Sale not found for this receipt');
    } finally {
      setLookupLoading(false);
    }
  };

  const setRowValue = (productId, key, value) => {
    setRowForm((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || { quantity: '', reason: '' }),
        [key]: value,
      },
    }));
  };

  const submitRefund = async () => {
    if (!sale) return;
    const payloadItems = [];
    for (const row of rows) {
      const form = rowForm[row.product_id];
      if (!form) continue;
      const qty = parseInt(form.quantity || '0', 10);
      if (!qty) continue;
      if (qty < 0) {
        toast.error(`Refund quantity cannot be negative for ${row.product_name}`);
        return;
      }
      if (qty > row.remaining_qty) {
        toast.error(`Refund for ${row.product_name} exceeds remaining quantity`);
        return;
      }
      payloadItems.push({
        product_id: row.product_id,
        quantity: qty,
        reason: (form.reason || '').trim() || null,
      });
    }

    if (!payloadItems.length) {
      toast.error('Enter at least one refund item quantity');
      return;
    }

    setSubmitLoading(true);
    try {
      const idem = crypto.randomUUID();
      const payload = {
        items: payloadItems,
        notes: notes.trim() || null,
        idempotency_key: idem,
      };
      await api.post(`/sales/${sale.id}/refunds`, payload, {
        headers: {
          'Idempotency-Key': idem,
        },
      });
      toast.success('Refund submitted successfully');
      const refundsRes = await backendApi.sales.listRefunds(api, sale.id);
      setRefunds(refundsRes.data || []);
      setRowForm({});
      setNotes('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit refund');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!canIssueRefunds) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Refund Center</CardTitle>
          <CardDescription>You do not have permission to issue refunds.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="refunds-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Refund Center</h1>
        <p className="text-muted-foreground">Lookup receipt, select items, and process controlled refunds.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Find Sale By Receipt</CardTitle>
          <CardDescription>Use the printed receipt number to open sale details and refunds.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-[1fr_auto] gap-3">
            <Input
              placeholder="e.g., KDL-20260228145500-ABC123"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              data-testid="refund-receipt-input"
            />
            <Button onClick={loadSaleByReceipt} disabled={lookupLoading} data-testid="refund-lookup-btn">
              {lookupLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Lookup
            </Button>
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {sale ? (
        <Card>
          <CardHeader>
            <CardTitle>Sale #{sale.receipt_number}</CardTitle>
            <CardDescription>
              Total: <strong>{formatCurrency(sale.total_amount)}</strong> | Method: <Badge variant="outline">{sale.payment_method}</Badge>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Sold</TableHead>
                  <TableHead>Already Refunded</TableHead>
                  <TableHead>Remaining</TableHead>
                  <TableHead>Refund Qty</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.product_id}>
                    <TableCell className="font-medium">{row.product_name}</TableCell>
                    <TableCell>{row.sold_qty}</TableCell>
                    <TableCell>{row.refunded_qty}</TableCell>
                    <TableCell>
                      <Badge variant={row.remaining_qty > 0 ? 'default' : 'secondary'}>{row.remaining_qty}</Badge>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max={row.remaining_qty}
                        value={rowForm[row.product_id]?.quantity || ''}
                        onChange={(e) => setRowValue(row.product_id, 'quantity', e.target.value)}
                        className="w-24"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Optional reason"
                        value={rowForm[row.product_id]?.reason || ''}
                        onChange={(e) => setRowValue(row.product_id, 'reason', e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="space-y-2">
              <Label>Refund Notes</Label>
              <Textarea
                placeholder="Optional notes for this refund"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={submitRefund} disabled={submitLoading} className="neon-red" data-testid="submit-refund-btn">
                {submitLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                Submit Refund
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {sale ? (
        <Card>
          <CardHeader>
            <CardTitle>Refund History</CardTitle>
            <CardDescription>Previous refunds recorded against this sale.</CardDescription>
          </CardHeader>
          <CardContent>
            {!refunds.length ? (
              <p className="text-sm text-muted-foreground">No refunds recorded yet.</p>
            ) : (
              <div className="space-y-4">
                {refunds.map((refund, refundIndex) => {
                  const refundNotes = typeof refund.notes === 'string' ? refund.notes.trim() : '';
                  const refundItems = refund.items || [];

                  return (
                    <article key={refund.id} className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
                      <div className="flex flex-col gap-3 border-b border-border/60 bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground">Refund {getRefundReference(refundIndex)}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formatBusinessDateTime(refund.created_at, { hour12: true })}
                          </p>
                        </div>
                        <div className="sm:text-right">
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Refund amount</p>
                          <p className="mt-1 text-lg font-semibold text-foreground">{formatCurrency(refund.total_amount)}</p>
                        </div>
                      </div>

                      <div className="space-y-4 p-4 sm:p-5">
                        {refundItems.length ? (
                          <>
                            <div className="hidden overflow-hidden rounded-lg border border-border/60 sm:block">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                                    <TableHead>Product</TableHead>
                                    <TableHead className="w-28">Quantity</TableHead>
                                    <TableHead>Reason</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {refundItems.map((item, itemIndex) => (
                                    <TableRow key={`${refund.id}-${itemIndex}`}>
                                      <TableCell className="font-medium">{getRefundProductName(item)}</TableCell>
                                      <TableCell>{item.quantity}</TableCell>
                                      <TableCell className="text-muted-foreground">
                                        {item.reason?.trim() || 'No reason provided'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>

                            <div className="divide-y divide-border/60 rounded-lg border border-border/60 sm:hidden">
                              {refundItems.map((item, itemIndex) => (
                                <div key={`${refund.id}-mobile-${itemIndex}`} className="space-y-3 p-4">
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground">Product</p>
                                    <p className="mt-1 break-words text-sm font-medium">{getRefundProductName(item)}</p>
                                  </div>
                                  <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-3">
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground">Quantity</p>
                                      <p className="mt-1 text-sm">{item.quantity}</p>
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-xs font-medium text-muted-foreground">Reason</p>
                                      <p className="mt-1 break-words text-sm">{item.reason?.trim() || 'No reason provided'}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">No refund items recorded.</p>
                        )}

                        {refundNotes ? (
                          <div className="rounded-lg bg-muted/40 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
                            <p className="mt-2 whitespace-pre-wrap break-words text-sm text-foreground">{refundNotes}</p>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default RefundsPage;
